import { useState, useRef, useEffect } from 'react'
import { useBranches } from '../../hooks/useAutomation'
import clsx from 'clsx'

/**
 * BranchSelector
 *
 * Props:
 *   repositoryId   — repo ID for API fetch
 *   value          — currently selected branch name (null = use default)
 *   defaultBranch  — repo's default branch (shown as fallback label)
 *   onChange       — (branchName | null) => void
 *   disabled       — bool
 *   label          — field label string
 *   hint           — help text below selector
 */
export default function BranchSelector({
  repositoryId,
  value,
  defaultBranch = 'main',
  onChange,
  disabled = false,
  label = 'Branch',
  hint,
}) {
  const { branches, loading, error, fromCache, cachedAt, refresh } = useBranches(repositoryId)
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const dropRef             = useRef(null)

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (!dropRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = branches.filter(b =>
    b.toLowerCase().includes(search.toLowerCase())
  )

  const displayValue = value || defaultBranch
  const isDefault    = !value || value === defaultBranch

  const select = (branch) => {
    onChange(branch === defaultBranch ? null : branch)
    setOpen(false)
    setSearch('')
  }

  return (
    <div>
      {label && (
        <label className="label-specze">{label}</label>
      )}

      {/* Trigger */}
      <div ref={dropRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={clsx(
            'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200',
            'bg-white/[0.04] border border-white/10 hover:border-purple-500/40',
            'focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/40',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Branch icon */}
            <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M6 3v12m0 0a3 3 0 100 6 3 3 0 000-6zm0 0h6a3 3 0 003-3V9m0 0a3 3 0 100-6 3 3 0 000 6z"/>
            </svg>
            <span className="text-white font-mono truncate">{displayValue}</span>
            {isDefault && (
              <span className="text-[10px] font-semibold text-purple-400/70 bg-purple-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0">
                default
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {loading && (
              <svg className="w-3.5 h-3.5 text-white/30 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            )}
            <svg className={clsx('w-4 h-4 text-white/40 transition-transform', open && 'rotate-180')}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute z-50 mt-1.5 w-full rounded-2xl border border-white/12 bg-cosmic-850/95 backdrop-blur-xl shadow-2xl overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-white/8">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
                <svg className="w-3.5 h-3.5 text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  autoFocus
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/25 outline-none"
                  placeholder="Search branches…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-52 overflow-y-auto py-1">
              {error ? (
                <div className="px-4 py-3 text-xs text-red-400">{error}</div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-3 text-xs text-white/30">No branches found</div>
              ) : (
                filtered.map(branch => {
                  const isSelected  = (value || defaultBranch) === branch
                  const isDefBranch = branch === defaultBranch
                  return (
                    <button
                      key={branch}
                      type="button"
                      onClick={() => select(branch)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors',
                        isSelected
                          ? 'bg-purple-500/20 text-purple-300'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      )}
                    >
                      <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M6 3v12m0 0a3 3 0 100 6 3 3 0 000-6zm0 0h6a3 3 0 003-3V9m0 0a3 3 0 100-6 3 3 0 000 6z"/>
                      </svg>
                      <span className="font-mono flex-1 truncate">{branch}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isDefBranch && (
                          <span className="text-[10px] font-semibold text-blue-400/70 bg-blue-500/10 px-1.5 py-0.5 rounded-full">
                            default
                          </span>
                        )}
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                          </svg>
                        )}
                      </div>
                    </button>
                  )
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-white/6 flex items-center justify-between">
              <span className="text-[10px] text-white/25">
                {branches.length} branch{branches.length !== 1 ? 'es' : ''}
                {fromCache && cachedAt && ` · cached ${new Date(cachedAt).toLocaleTimeString()}`}
              </span>
              <button
                type="button"
                onClick={refresh}
                className="text-[10px] text-purple-400/70 hover:text-purple-300 flex items-center gap-1 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>

      {hint && <p className="text-[11px] text-white/35 mt-1.5">{hint}</p>}
    </div>
  )
}
